/**
 * Shared dependencies
 */
import type { StoreFunctions } from '../../shared/types/store';

/**
 * Internal dependencies
 */
import { deleteUserList, type DeleteUserListParams } from '../requests';

/**
 * Creates a new list for the current user and updates the store.
 * Also dispatches a ListEvent to notify other parts of the app.
 *
 * @param fn - Function to update the store state.
 * @param params - The parameters for creating the list.
 * @return Promise that resolves to the created user's list.
 */
export async function listsDelete(fn: StoreFunctions, params: DeleteUserListParams): Promise<void> {
	const {
		events: { ListEvent },
	} = window.valioApp;

	// Check if the list exists in the store before deletion
	const listToDelete = fn.get().data.lists[params.listId];

	if (!listToDelete) {
		throw new Error(`List with id ${params.listId} does not exist in the store`);
	}

	// Delete the list via the API
	const success = await deleteUserList(params);

	if (!success) {
		throw new Error(`Failed to delete list with id ${params.listId}`);
	}

	// Update the store to remove the deleted list
	fn.set((state) => ({
		data: {
			lists: Object.fromEntries(
				Object.entries(state.data.lists).filter(([id]) => id !== params.listId)
			),
		},
	}));

	ListEvent.delete({
		data: {
			id: params.listId,
			type: listToDelete.type,
		},
	});
}
